/**
 * TypeScript интерфейсы для Jataka Calculator API
 * Основано на FastAPI Pydantic моделях backend
 */

// ===== API REQUEST TYPES =====

/**
 * Запрос для расчета джатака (натальной карты)
 * Соответствует JatakaRequest из FastAPI backend
 */
export interface JatakaRequest {
  /** Дата и время рождения в формате ISO 8601 (YYYY-MM-DDTHH:mm:ss) */
  datetime: string;
  
  /** Широта в десятичных градусах (-90 до 90) */
  latitude: number;
  
  /** Долгота в десятичных градусах (-180 до 180) */
  longitude: number;
  
  /** Часовой пояс в часах от UTC (-12 до 12) */
  timezone_offset: number;
  
  /** Название места (опционально) */
  place_name?: string;
  
  /** Высота над уровнем моря в метрах (опционально, 0-10000) */
  altitude?: number;
}

// ===== API RESPONSE TYPES =====

/**
 * Расширенная информация о Титхи (5 видов)
 */
export interface TithiKindInfo {
  /** Название вида (Nanda, Bhadra, Jaya, Rikta, Purna) */
  name: string;
  
  /** Английское название вида */
  name_en: string;
  
  /** Описание вида и его значения */
  description: string;
  
  /** Номера титхи этого вида */
  tithis: number[];
  
  /** Природа вида */
  nature: string;
  
  /** Качества, которые дает этот вид */
  qualities: string[];
}

/**
 * Полная информация о конкретном Титхи
 */
export interface TithiInfo {
  /** Номер титхи (1-30) */
  number: number;
  
  /** Название титхи */
  name: string;
  
  /** Пакша (Shukla/Krishna) */
  paksha: string;
  
  /** Номер титхи в пакше (1-15) */
  paksha_tithi: number;
  
  /** Управляющая планета */
  ruling_planet: string;
  
  /** Божество-покровитель */
  patron_deity: string;
  
  /** Вид титхи */
  kind: string;
  
  /** Описание вида */
  kind_description: string;
  
  /** Качества вида */
  kind_qualities: string[];
  
  /** Процент завершения */
  completion_percentage: number;
  
  /** Оставшиеся градусы */
  remaining_degrees: number;
  
  /** Является ли особым титхи */
  is_special: boolean;
}

/**
 * Основная информация о натальной карте
 */
export interface BasicInfo {
  /** Название карты */
  chart_name?: string;
  
  /** Дата рождения в человекочитаемом формате */
  date: string;
  
  /** Время рождения */
  time: string;

  /** Полная дата-время в ISO 8601 с микросекундами и timezone */
  datetime_iso: string;

  /** Часовой пояс */
  timezone: string;
  
  /** Координаты места в формате DMS */
  location: string;
  
  /** Название места */
  place_name?: string;
  
  /** Высота над уровнем моря */
  altitude: string;
  
  /** Широта в десятичных градусах */
  latitudeValue: number;
  
  /** Долгота в десятичных градусах */
  longitudeValue: number;
}

/**
 * Информация о панчанге (5 лимбов ведической астрологии)
 */
export interface PanchangaInfo {
  /** Лунный год и месяц */
  lunar_year_month: string;
  
  /** Титхи (лунный день) с информацией о 5 видах */
  tithi: string;
  
  /** День недели */
  weekday: string;
  
  /** Накшатра (лунная стоянка) */
  nakshatra: string;
  
  /** Йога (планетарная комбинация) */
  yoga: string;
  
  /** Карана (половина титхи) */
  karana: string;
  
  /** Господь хоры */
  hora_lord: string;
  
  /** Информация о мухурте (опционально) */
  muhurta?: string;
  
  /** Информация о гхати (опционально) */
  ghati?: string;
}

/**
 * Позиция планеты (граха) или Лагны
 */
export interface GrahaPosition {
  /** Название планеты/Лагны (санскрит) */
  name: string;
  
  /** Короткое название (Sy, Ch, Ma, Bu, Gu, Sk, Sa, Ra, Ke, Lg) */
  short_name: string;
  
  /** УНИФИЦИРОВАННАЯ ПОЗИЦИЯ в формате "rasi|degrees|longitude|speed" */
  position: string;
  
  /** Долгота в традиционном формате (например: 26 Mith 51' 09.12") */
  longitude_dms: string;
  
  /** Широта в градусах (0.0 для Лагны) */
  latitude: number;
  
  /** Ретроградное движение (false для Лагны) */
  is_retrograde: boolean;
  
  /** Номер раши (1-12) */
  rasi_number: number;
  
  /** Название раши (санскрит) */
  rasi_name: string;
  
  /** Номер накшатры (1-27) */
  nakshatra_number: number;
  
  /** Название накшатры (санскрит) */
  nakshatra_name: string;
  
  /** Пада накшатры (1-4) */
  nakshatra_pada: number;
  
  /** Раши в Навамше (санскрит) */
  navamsa_rasi: string;
  
  /** Номер мухурты */
  muhurta_num?: number;
  
  /** Правящая планета мухурты */
  muhurta_graha?: string;
  
  /** Господин мухурты */
  muhurta_lord?: string;
}

/**
 * Позиции всех планет и Лагны
 */
export interface GrahaPositions {
  /** Список позиций планет и Лагны */
  planets: GrahaPosition[];
}

/**
 * Астрономическая информация
 */
export interface AstronomicalInfo {
  /** Время восхода солнца */
  sunrise: string;
  
  /** Долгота Сурьи на время восхода (DMS формат) */
  sunrise_surya_longitude: string;
  
  /** Время захода солнца */
  sunset: string;
  
  /** Джанма гхати */
  janma_ghatis: string;
  
  /** Айанамша */
  ayanamsa: string;
  
  /** Система айанамши */
  ayanamsa_system: string;
  
  /** Звездное время */
  sidereal_time: string;
  
  /** Юлианский день */
  julian_day: number;
}

// ===== DETAILED PANCHANGA TYPES =====

/**
 * Детальная информация о Самват
 */
export interface SamvatDetails {
  /** Год Самват */
  samvat_year: number;
  
  /** Название Самват */
  samvat_name: string;
  
  /** Позиция Юпитера в знаке */
  jupiter_position: string;
  
  /** Начало лунного года */
  lunar_year_start: string;
  
  /** Цикл 60 лет */
  cycle_60: string;
  
  /** Дни с начала года */
  days_since_year_start: number;
}

/**
 * Детальная информация о Масе
 */
export interface MasaDetails {
  /** Номер месяца (1-12) */
  masa_number: number;
  
  /** Название месяца */
  masa_name: string;
  
  /** Альтернативное название */
  masa_alternative_name: string;
  
  /** Божество-покровитель */
  ruling_deity: string;
  
  /** Сезон (Рitu) */
  season: string;
  
  /** Является ли Адхика (дополнительным) */
  is_adhika: boolean;
  
  /** Оставшиеся дни в месяце */
  remaining_days: number;
}

/**
 * Детальная информация о Титхи
 */
export interface TithiDetails {
  /** Номер титхи (1-30) */
  tithi_number: number;
  
  /** Название титхи */
  tithi_name: string;
  
  /** Пакша (Shukla/Krishna) */
  paksha: string;
  
  /** Номер в пакше (1-15) */
  paksha_number: number;
  
  /** Управляющая планета */
  ruling_planet: string;
  
  /** Божество-покровитель */
  patron_deity: string;
  
  /** Вид титхи */
  kind: string;
  
  /** Описание вида */
  kind_description: string;
  
  /** Процент завершения */
  completion_percentage: number;
  
  /** Оставшиеся градусы */
  remaining_degrees: number;
}

/**
 * Детальная информация о Накшатре
 */
export interface NakshatraDetails {
  /** Номер накшатры (1-27) */
  nakshatra_number: number;
  
  /** Название накшатры */
  nakshatra_name: string;
  
  /** Управляющая планета */
  ruling_planet: string;
  
  /** Божество-покровитель */
  patron_deity: string;
  
  /** Символ */
  symbol: string;
  
  /** Природа */
  nature: string;
  
  /** Пада (1-4) */
  pada: number;
  
  /** Процент завершения */
  completion_percentage: number;
  
  /** Оставшиеся минуты */
  remaining_minutes: number;
}

/**
 * Детальная информация о Каране
 */
export interface KaranaDetails {
  /** Номер караны (1-11) */
  karana_number: number;
  
  /** Название караны */
  karana_name: string;
  
  /** Управляющая планета */
  ruling_planet: string;
  
  /** Божество-покровитель */
  patron_deity: string;
  
  /** Тип (подвижная/неподвижная) */
  type: string;
  
  /** Процент завершения */
  completion_percentage: number;
  
  /** Оставшиеся градусы */
  remaining_degrees: number;
}

/**
 * Детальная информация о Йоге
 */
export interface YogaDetails {
  /** Номер йоги (1-27) */
  yoga_number: number;
  
  /** Название йоги */
  yoga_name: string;
  
  /** Управляющая планета */
  ruling_planet: string;
  
  /** Божество-покровитель */
  patron_deity: string;
  
  /** Качество йоги */
  quality: string;
  
  /** Процент завершения */
  completion_percentage: number;
  
  /** Оставшиеся градусы */
  remaining_degrees: number;
}

/**
 * Детальная информация о Варе (дне недели)
 */
export interface VaraDetails {
  /** Номер дня недели (0-6, где 0=Sunday, 4=Thursday) */
  number: number;

  /** Название дня на санскрите */
  sanskrit: string;

  /** Название дня на английском */
  english: string;
  
  /** Управляющая планета */
  ruling_planet: string;

  /** Код планеты */
  planet_code: string;
}

/**
 * Детальная информация о Хоре
 */
export interface HoraDetails {
  /** Текущая планета-управитель хоры */
  current_planet: string;

  /** Номер хоры (1-24) */
  hour_number: number;

  /** Период (Day/Night) */
  period: string;

  /** Duration of current hora in seconds */
  hora_duration_sec?: number;

  /** Seconds elapsed since current hora started */
  hora_elapsed_sec?: number;

  /** Start time of current hora (HH:MM:SS) */
  hora_start?: string;

  /** End time of current hora (HH:MM:SS) */
  hora_end?: string;
}

/**
 * Детальная информация о Мухурте
 */
export interface MuhurtaDetails {
  /** Номер мухурты (1-30) */
  number: number;
  
  /** Название мухурты */
  name: string;
  
  /** Управляющее божество */
  lord: string;
  
  /** Управляющая планета (грaha) */
  graha_ruler: string;
  
  /** Текущий гхати */
  current_ghati?: string;
  
  /** Процент завершения */
  completion: number;
  
  /** Прошедшее время */
  elapsed: number;

  /** Долгота чакры в DMS формате */
  chakra_longitude: string;
}

/**
 * Детальная информация о Гхати
 */
export interface GhatiDetails {
  /** Номер гхати (1-60) */
  ghati_number: number;
  
  /** Название гхати */
  ghati_name: string;
  
  /** Управляющее божество */
  ruling_deity: string;
  
  /** Родительская мухурта */
  parent_muhurta: string;
  
  /** Время начала */
  start_time: string;
  
  /** Время окончания */
  end_time: string;
  
  /** Процент завершения */
  completion_percentage: number;
}

/**
 * Детальная информация о панчанге
 */
export interface DetailedPanchangaInfo {
  /** Детали Самват */
  samvat_details: SamvatDetails;
  
  /** Детали Масы */
  masa_details: MasaDetails;
  
  /** Детали Титхи */
  tithi_details: TithiDetails;
  
  /** Детали Накшатры */
  nakshatra_details: NakshatraDetails;
  
  /** Детали Караны */
  karana_details: KaranaDetails;
  
  /** Детали Йоги */
  yoga_details: YogaDetails;
  
  /** Детали Вары */
  vara_details: VaraDetails;
  
  /** Детали Хоры */
  hora_details: HoraDetails;
  
  /** Детали Мухурты */
  muhurta_details: MuhurtaDetails;
  
  /** Детали Гхати */
  ghati_details: GhatiDetails;
}

/**
 * Полный ответ API с расчетом джатака
 */
export interface JatakaResponse {
  /** Основная информация */
  basic_info: BasicInfo;
  
  /** Панчанга */
  panchanga: PanchangaInfo;
  
  /** Детальная панчанга (опционально) */
  detailed_panchanga?: DetailedPanchangaInfo;
  
  /** Астрономические данные */
  astronomical: AstronomicalInfo;
  
  /** Позиции планет и Лагны */
  graha_positions: GrahaPositions;
  
  /** Метаданные расчета (опционально) */
  calculation_metadata?: Record<string, any>;
}

// ===== ERROR TYPES =====

/**
 * Ошибка API
 */
export interface ApiError {
  /** Тип ошибки */
  error: string;
  
  /** Сообщение об ошибке */
  message: string;
  
  /** Детали ошибки */
  details?: Record<string, any>;
}

/**
 * Результат операции API
 */
export interface ApiResult<T = any> {
  /** Успешность операции */
  success: boolean;
  
  /** Данные (при успехе) */
  data?: T;
  
  /** Ошибка (при неудаче) */
  error?: ApiError;
  
  /** HTTP статус код */
  status?: number;
}

// ===== HEALTH CHECK TYPES =====

/**
 * Ответ проверки работоспособности API
 */
export interface HealthResponse {
  /** Статус работы */
  status: 'healthy' | 'unhealthy';
  
  /** Название сервиса */
  service: string;
  
  /** Версия API */
  version: string;
  
  /** Используемый калькулятор */
  calculator?: string;
  
  /** Поддерживаемые расчеты */
  supported_calculations?: string[];
}

/**
 * Информация об API
 */
export interface ApiInfo {
  /** Название API */
  name: string;
  
  /** Версия */
  version: string;
  
  /** Описание */
  description: string;
  
  /** Доступные endpoints */
  endpoints: Record<string, string>;
  
  /** Документация */
  documentation: {
    swagger: string;
    redoc: string;
  };
  
  /** Возможности калькулятора */
  calculator_features: string[];
}

// ===== CONFIGURATION TYPES =====

/**
 * Конфигурация API клиента
 */
export interface ApiConfig {
  /** Базовый URL API */
  baseUrl: string;
  
  /** Таймаут запросов в миллисекундах */
  timeout: number;
  
  /** Количество повторных попыток */
  retries: number;
  
  /** Включить логирование */
  enableLogging: boolean;
}



// ===== TYPE GUARDS =====

/**
 * Проверка является ли объект API ошибкой
 */
export function isApiError(obj: any): obj is ApiError {
  return obj && typeof obj === 'object' && 'error' in obj && 'message' in obj;
}

/**
 * Проверка является ли результат успешным
 */
export function isSuccessResult<T>(result: ApiResult<T>): result is ApiResult<T> & { success: true; data: T } {
  return result.success && result.data !== undefined;
}

/**
 * Проверка является ли результат неуспешным
 */
export function isErrorResult<T>(result: ApiResult<T>): result is ApiResult<T> & { success: false; error: ApiError } {
  return !result.success && result.error !== undefined;
}

// ===== TITHI UTILITIES =====

/**
 * Получить вид Титхи по номеру
 */
export function getTithiKind(tithiNumber: number): string {
  // Приводим к диапазону 1-15 для определения вида
  const normalizedTithi = tithiNumber <= 15 ? tithiNumber : tithiNumber - 15;
  
  if ([1, 6, 11].includes(normalizedTithi)) return 'Nanda';
  if ([2, 7, 12].includes(normalizedTithi)) return 'Bhadra';
  if ([3, 8, 13].includes(normalizedTithi)) return 'Jaya';
  if ([4, 9, 14].includes(normalizedTithi)) return 'Rikta';
  if ([5, 10, 15].includes(normalizedTithi)) return 'Purna';
  
  return 'Nanda'; // По умолчанию
}

/**
 * Получить информацию о виде Титхи по номеру
 */
export function getTithiKindInfo(tithiNumber: number): TithiKindInfo {
  const kindName = getTithiKind(tithiNumber);
  
  // Inline minimal data instead of large TITHI_KINDS constant
  const kinds: Record<string, TithiKindInfo> = {
    'Nanda': { name: 'Nanda', name_en: 'Joyful', description: 'Grants joy and happiness', tithis: [1, 6, 11, 16, 21, 26], nature: 'Joyful', qualities: ['joy', 'happiness'] },
    'Bhadra': { name: 'Bhadra', name_en: 'Auspicious', description: 'Grants luck and blessing', tithis: [2, 7, 12, 17, 22, 27], nature: 'Auspicious', qualities: ['luck', 'blessing'] },
    'Jaya': { name: 'Jaya', name_en: 'Victory', description: 'Brings victory', tithis: [3, 8, 13, 18, 23, 28], nature: 'Victory', qualities: ['achievement', 'victory'] },
    'Rikta': { name: 'Rikta', name_en: 'Empty', description: 'May bring empty results', tithis: [4, 9, 14, 19, 24, 29], nature: 'Empty', qualities: ['emptiness', 'freedom'] },
    'Purna': { name: 'Purna', name_en: 'Complete', description: 'Promotes completeness', tithis: [5, 10, 15, 20, 25, 30], nature: 'Complete', qualities: ['completeness', 'abundance'] }
  };
  
  return kinds[kindName];
}

/**
 * Парсинг строки Титхи из API для извлечения информации о виде
 */
export function parseTithiString(tithiString: string): {
  name: string;
  rulingPlanet: string;
  kind?: string;
  completion: number;
} {
  // Формат: "Pratipada (Su) - Nanda (33.15% left)"
  const match = tithiString.match(/^(.+?)\s*\((.+?)\)(?:\s*-\s*(.+?))?\s*\((.+?)%\s*left\)/);
  
  if (match) {
    return {
      name: match[1].trim(),
      rulingPlanet: match[2].trim(),
      kind: match[3]?.trim(),
      completion: 100 - parseFloat(match[4])
    };
  }
  
  // Fallback для старого формата без вида
  const fallbackMatch = tithiString.match(/^(.+?)\s*\((.+?)\)\s*\((.+?)%\s*left\)/);
  if (fallbackMatch) {
    return {
      name: fallbackMatch[1].trim(),
      rulingPlanet: fallbackMatch[2].trim(),
      completion: 100 - parseFloat(fallbackMatch[3])
    };
  }
  
  return {
    name: tithiString,
    rulingPlanet: '',
    completion: 0
  };
}

// ===== MOTION API TYPES =====

/**
 * Данные о движении планеты в конкретный момент времени (новый формат API)
 * Формат строки: "rasi|degrees|longitude|speed"
 * Пример: "8|17.089408|257.089408|1.011949"
 */
export interface PlanetMotionData {
  /** Долгота в градусах */
  longitude: number;
  
  /** Скорость движения в градусах/день */
  speed: number;
  
  /** Ошибка расчета (если есть) */
  error?: string;
}

/**
 * Данные планеты в новом формате API (строка)
 */
export type PlanetMotionString = string; // "rasi|degrees|longitude|speed"

/**
 * Точка данных движения для конкретного времени (новый формат API)
 */
export interface MotionDataPoint {
  /** Временная метка в формате ISO */
  timestamp: string;
  
  /** Данные движения планет в новом формате (строки "rasi|degrees|longitude|speed") */
  Lg?: PlanetMotionString;   // Lagna
  Sy?: PlanetMotionString;   // Surya
  Ch?: PlanetMotionString;   // Chandra
  Ma?: PlanetMotionString;   // Mangala
  Bu?: PlanetMotionString;   // Budha
  Gu?: PlanetMotionString;   // Guru
  Sk?: PlanetMotionString;   // Shukra (Sukra)
  Sa?: PlanetMotionString;   // Sani
  Ra?: PlanetMotionString;   // Rahu
  Ke?: PlanetMotionString;   // Ketu
}

/**
 * Информация о кэше движения планет
 */
export interface MotionCacheInfo {
  /** Был ли запрос закэширован */
  cached: boolean;
  
  /** Ключ кэша */
  cache_key: string;
  
  /** Возраст кэша в секундах (только для закэшированных данных) */
  age_seconds?: number;
}

/**
 * Координаты наблюдателя
 */
export interface ObserverLocation {
  /** Широта в градусах */
  latitude: number;
  
  /** Долгота в градусах */
  longitude: number;
}

/**
 * Ответ Motion Buffer API
 */
export interface MotionBufferResponse {
  /** Начальное время буфера */
  start_time: string;
  
  /** Продолжительность в минутах */
  duration_minutes: number;
  
  /** Интервал между точками в секундах */
  interval_seconds: number;
  
  /** Общее количество точек данных */
  total_points: number;
  
  /** Координаты наблюдателя */
  observer_location: ObserverLocation;
  
  /** Массив точек данных движения */
  data: MotionDataPoint[];
  
  /** Информация о кэше */
  cache_info: MotionCacheInfo;
}

/**
 * Статистика кэша движения планет
 */
export interface MotionCacheStats {
  /** Статистика кэша */
  cache_stats: {
    /** Общее количество записей */
    total_entries: number;
    
    /** Действительные записи */
    valid_entries: number;
    
    /** Истекшие записи */
    expired_entries: number;
    
    /** TTL кэша в секундах */
    cache_ttl_seconds: number;
  };
  
  /** Производительность */
  performance: {
    /** Потенциальный процент попаданий в кэш */
    cache_hit_potential: string;
    
    /** Эффективность памяти */
    memory_efficiency: string;
    
    /** Стратегия очистки */
    cleanup_strategy: string;
  };
}

/**
 * Ответ очистки кэша
 */
export interface MotionCacheClearResponse {
  /** Статус операции */
  status: string;
  
  /** Сообщение */
  message: string;
  
  /** Количество очищенных записей */
  entries_cleared: number;
}